import {
  ExtendedWarrantySchema,
  OfferingSelection,
  offeringSelectionSchema,
} from "@redotech/redo-return-warranties-common/extended-warranty";
import { PurchaseChannel } from "@redotech/redo-return-warranties-common/warranties";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import {
  ReturnProduct,
  ReturnProductSchema,
} from "./draft-return/draft-return-items";
import { ReturnFlow, Step } from "./return-flow";
import { Condition, RegistrationParameters } from "./return-flow/condition";
import { LineItem, LineItemSchema_ } from "./shopify-order";
import {
  ProductVariantResponse as ProductVariantResponseInput,
  SubmitWarrantyRegistrationInput,
} from "./warranties/submit-warranty-registration-schema";

export enum RegistrationUpsellType {
  EW_UPSELL = "ew_upsell",
  EMAIL_OPT_IN_UPSELL = "email_opt_in_upsell",
}

export const RETAIL_ORDER_PREFIX = "RTL";
export const RECYCLE_REQUEST_PREFIX = "Recycle";

// Schema definitions
const UserInfoSchema_ = z.object({
  email: z.string(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  emailOptIn: z.boolean().optional(),
  phoneNumber: z.string().optional(),
});
export const UserInfoSchema: z.ZodType<UserInfo> = UserInfoSchema_;
export interface UserInfo extends z.infer<typeof UserInfoSchema_> {}

const UserAddressSchema_ = z.object({
  street1: z.string().optional(),
  street2: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  country: z.string().optional(), // CountryCode is a string union
  zip: z.string().optional(),
});
export const UserAddressSchema: z.ZodType<UserAddress> = UserAddressSchema_;
export interface UserAddress extends z.infer<typeof UserAddressSchema_> {}

const ProductVariantResponseSchema_ = z.object({
  type: createEnum(["images", "text_area", "videos", "multiple_choice"]),
  value: z.union([z.string(), z.array(z.string())]),
  message: z.string(),
});
export const ProductVariantResponseSchema: z.ZodType<ProductVariantResponse> =
  ProductVariantResponseSchema_;
export interface ProductVariantResponse
  extends z.infer<typeof ProductVariantResponseSchema_> {}

export type OnlineReferenceId = `${string}_${number}`;

const EligibleLineItemSchema_ = LineItemSchema_.extend({
  referenceId: z.string(),
  lineItemCollectionTitles: z.array(z.string()),
  originalLineItemQuantity: z.number(),
});
export const EligibleLineItemSchema: z.ZodType<EligibleLineItem> =
  EligibleLineItemSchema_;
export interface EligibleLineItem
  extends z.infer<typeof EligibleLineItemSchema_> {}

const SharedSourceDetailsSchema_ = z.object({
  channelName: z.string().optional(),
});

const OnlineSourceDetailsSchema_ = SharedSourceDetailsSchema_.extend({
  purchaseChannel: z.literal(PurchaseChannel.ONLINE),
  orderId: z.string(),
  existingExtendedWarranties: z.array(ExtendedWarrantySchema).optional(),
  eligibleLineItems: z.array(EligibleLineItemSchema_).optional(),
  referenceId: z.string().optional(),
});
export const OnlineSourceDetailsSchema: z.ZodType<OnlineSourceDetails> =
  OnlineSourceDetailsSchema_;
export interface OnlineSourceDetails
  extends z.infer<typeof OnlineSourceDetailsSchema_> {}

const RetailSourceDetailsSchema_ = SharedSourceDetailsSchema_.extend({
  purchaseChannel: z.literal(PurchaseChannel.RETAIL),
  isMerchantCreated: z.boolean(),
  isClaimOnly: z.boolean(),
});
export const RetailSourceDetailsSchema: z.ZodType<RetailSourceDetails> =
  RetailSourceDetailsSchema_;
export interface RetailSourceDetails
  extends z.infer<typeof RetailSourceDetailsSchema_> {}

const SourceDetailsSchema_ = z.discriminatedUnion("purchaseChannel", [
  OnlineSourceDetailsSchema_,
  RetailSourceDetailsSchema_,
]);
export const SourceDetailsSchema: z.ZodType<SourceDetails> =
  SourceDetailsSchema_;
export type SourceDetails = z.infer<typeof SourceDetailsSchema_>;

const DraftRegistrationSchema_ = z.object({
  products: z.array(ReturnProductSchema),
  source: SourceDetailsSchema_,
  flow: z.any(),
  userInfo: UserInfoSchema_.optional(),
  userAddress: UserAddressSchema_.optional(),
  addressConfirmed: z.boolean().optional(),
  productVariantResponses: z
    .record(z.string(), z.array(ProductVariantResponseSchema_))
    .optional(),
  offeringSelections: z.record(z.string(), offeringSelectionSchema).optional(),
  cantFindItemDetails: z
    .object({
      productName: z.string(),
      photos: z.array(z.string()),
      price: z.string().optional(),
    })
    .optional(),
});
export const DraftRegistrationSchema: z.ZodType<DraftRegistration> =
  DraftRegistrationSchema_;
export interface DraftRegistration
  extends z.infer<typeof DraftRegistrationSchema_> {}

export function addProduct(
  draftRegistration: DraftRegistration,
  product: ReturnProduct,
): DraftRegistration {
  const existingProduct = draftRegistration.products.find(
    (p) => p.id === product.id,
  );
  const sameVariant = existingProduct?.variantId === product.variantId;
  if (existingProduct && sameVariant) {
    return draftRegistration;
  }
  if (existingProduct && !sameVariant) {
    const newProducts = draftRegistration.products.map((p) =>
      p.id === product.id ? product : p,
    );
    return { ...draftRegistration, products: newProducts };
  }
  return {
    ...draftRegistration,
    products: [...draftRegistration.products, product],
  };
}

export function generateOnlineReferenceId(
  lineItem: LineItem,
  index: number,
): OnlineReferenceId {
  return `${lineItem.id}_${index}`;
}

export function parseOnlineReferenceId(referenceId: OnlineReferenceId): {
  lineItemId: string;
  index: number;
} {
  const [lineItemId, index] = referenceId.split("_");
  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  return { lineItemId, index: parseInt(index) };
}

export function isOnlineReferenceId(
  referenceId: string,
): referenceId is OnlineReferenceId {
  return referenceId.match(/^.+_\d+$/) !== null;
}

export function addSourceDetails(
  draftRegistration: DraftRegistration,
  sourceDetails: SourceDetails,
): DraftRegistration {
  return { ...draftRegistration, source: sourceDetails };
}

export function addEligibleLineItem(
  draftRegistration: DraftRegistration,
  eligibleLineItem: EligibleLineItem,
): DraftRegistration {
  let newDraftRegistration = clearProducts(draftRegistration);
  newDraftRegistration = addProduct(
    newDraftRegistration,
    shopifyLineItemToReturnProduct(eligibleLineItem),
  );
  const sourceDetails = draftRegistration.source;
  if (sourceDetails.purchaseChannel === PurchaseChannel.ONLINE) {
    sourceDetails.referenceId = eligibleLineItem.referenceId;
  }
  newDraftRegistration = addSourceDetails(newDraftRegistration, sourceDetails);
  return newDraftRegistration;
}

export function addOfferingSelection(
  draftRegistration: DraftRegistration,
  variantId: string,
  offeringSelection: OfferingSelection,
): DraftRegistration {
  return {
    ...draftRegistration,
    offeringSelections: {
      ...draftRegistration.offeringSelections,
      [variantId]: offeringSelection,
    },
  };
}

// This is a terrible mapping, we should convert the line item to a return product on the backend
// or change the registration product to be more generic.
export function shopifyLineItemToReturnProduct(
  lineItem: EligibleLineItem,
): ReturnProduct {
  return {
    id: lineItem.product_id?.toString() ?? "",
    title: lineItem.title,
    variantTitle: lineItem.variant_title ?? "",
    variantId: lineItem.variant_id?.toString() ?? "",
    image: lineItem.image?.src ?? "",
    price: lineItem.price,
    tax: lineItem.tax_lines.map((taxLine) => taxLine.title).join(", "),
    productTags: lineItem.properties.map((property) => property.name ?? ""),
    productType: "",
    productVariantMetafields: {},
    // TODO: This will need to be updated when we want to do shopify EW upsell
    collections: lineItem.lineItemCollectionTitles,
    sku: lineItem.sku ?? "",
    quantity: lineItem.quantity,
    returnValue: lineItem.price,
    currency: lineItem.price_set?.presentment_money?.currency_code ?? "",
    deliveredOn: new Date(),
    fulfilledOn: new Date(),
    grams: lineItem.grams ?? 0,
    lastFulfillmentUpdateOn: new Date(),
    shipmentLocationId: "",
    productId: lineItem.product_id?.toString() ?? "",
    productDiscounts: [],
    productDiscountedAmount: 0,
    loyaltyAmount: 0,
    productProperties: {},
    extendedWarranty: undefined,
    variantData: { productTitle: "", tags: [], options: [], variants: [] },
    fulfillmentOrigin: undefined,
    isUnbundled: false,
    isExpandedPrimaryProduct: false,
    originalPrice: undefined,
    taxRate: 0,
  };
}

export function clearProducts(
  draftRegistration: DraftRegistration,
): DraftRegistration {
  return { ...draftRegistration, products: [] };
}

export function addChannelName(
  draftRegistration: DraftRegistration,
  channelName: string,
): DraftRegistration {
  return {
    ...draftRegistration,
    source: { ...draftRegistration.source, channelName },
  };
}

export function addUserInfo(
  draftRegistration: DraftRegistration,
  userInfo: UserInfo,
): DraftRegistration {
  return { ...draftRegistration, userInfo };
}

export function addUserAddress(
  draftRegistration: DraftRegistration,
  userAddress: UserAddress,
): DraftRegistration {
  return { ...draftRegistration, userAddress };
}

export function addProductVariantResponses(
  draftRegistration: DraftRegistration,
  variantId: string,
  responses: ProductVariantResponse[],
): DraftRegistration {
  return {
    ...draftRegistration,
    productVariantResponses: {
      ...draftRegistration.productVariantResponses,
      [variantId]: responses,
    },
  };
}

export function getPrimaryProduct(
  draftRegistration: DraftRegistration | undefined,
): ReturnProduct | undefined {
  return draftRegistration?.products[0];
}

export function getProductVariantResponses(
  draftRegistration: DraftRegistration | undefined,
  variantId: string | undefined,
): ProductVariantResponse[] {
  if (!variantId || !draftRegistration?.productVariantResponses) {
    return [];
  }
  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  return draftRegistration.productVariantResponses[variantId];
}

function cleanProductVariantResponses(
  responses: ProductVariantResponse[],
): ProductVariantResponseInput[] {
  return responses.map((response) => ({
    ...response,
    value: Array.isArray(response.value)
      ? response.value.map((url) => url.toString())
      : response.value,
  }));
}

export function draftRegistrationToSubmitWarrantyRegistrationInput(
  draftRegistration: DraftRegistration | undefined,
): SubmitWarrantyRegistrationInput {
  const product = getPrimaryProduct(draftRegistration);
  if (!product) {
    throw new Error("No product found in draft registration");
  }
  const userInfo = draftRegistration?.userInfo;
  if (
    !userInfo ||
    !userInfo.firstName ||
    !userInfo.lastName ||
    !userInfo.email
  ) {
    throw new Error("No user info found in draft registration");
  }
  const userAddress = draftRegistration?.userAddress;
  if (
    !userAddress ||
    !userAddress.city ||
    !userAddress.country ||
    !userAddress.state ||
    !userAddress.street1 ||
    !userAddress.zip
  ) {
    throw new Error("No user address found in draft registration");
  }
  // TODO: Make payment logic here
  const productOfferingSelections =
    draftRegistration?.offeringSelections?.[product.variantId];
  const cantFindItemDetails = draftRegistration?.cantFindItemDetails;
  return {
    notifyCustomer: true,
    product,
    responses: cleanProductVariantResponses(
      getProductVariantResponses(draftRegistration, product.id),
    ),
    account: {
      firstName: userInfo.firstName,
      lastName: userInfo.lastName,
      email: userInfo.email,
      phoneNumber: userInfo.phoneNumber || undefined,
      address: {
        city: userAddress.city,
        country: userAddress.country,
        state: userAddress.state,
        street1: userAddress.street1,
        street2: userAddress.street2 ?? "",
        zip: userAddress.zip,
      },
    },
    optInMarketing: userInfo.emailOptIn ?? false,
    source: draftRegistration?.source,
    offeringSelection: productOfferingSelections,
    requiresProductValidation: cantFindItemDetails ? true : undefined,
    customerProvidedProductName: cantFindItemDetails?.productName,
    customerProvidedProductPhotos: cantFindItemDetails?.photos.length
      ? cantFindItemDetails.photos
      : undefined,
    customerProvidedProductPrice: cantFindItemDetails?.price || undefined,
  };
}
export function walkFlow({
  draftRegistration,
  flow,
  start,
}: {
  draftRegistration: DraftRegistration | undefined;
  flow: ReturnFlow;
  start: number;
}): { path: number[] } {
  const path = [];
  let i = start;
  while (i < flow.steps.length) {
    path.push(i);
    const step = flow.steps[i];
    switch (step?.type) {
      case Step.CONDITION: {
        const value = Condition.evaluateRegistration(
          step.condition,
          buildConditionParams(
            draftRegistration,
            getPrimaryProduct(draftRegistration),
          ),
        );
        i = value ? step.nextTrue! : step.nextFalse!;
        break;
      }
      case Step.INPUT:
      case Step.MULTIPLE_CHOICE:
      case Step.INFORMATION:
      case Step.SUBMIT_WARRANTY_REGISTRATION:
        return { path };
      default:
        throw new Error(
          `Invalid registration flow step type: ${step?.type.description}`,
        );
    }
  }
  return { path };
}

function buildConditionParams(
  draftRegistration: DraftRegistration | undefined,
  product: ReturnProduct | undefined,
): RegistrationParameters {
  return {
    customerCountry: draftRegistration?.userAddress?.country ?? "Unknown",
    customerTags: [],
    productProperties: Object.keys(product?.productProperties ?? {}),
    productTags: product?.productTags ?? [],
    productType: product?.productType ?? "Unknown",
    sku: product?.sku ? [product.sku] : [],
    purchaseChannel:
      draftRegistration?.source?.purchaseChannel ?? PurchaseChannel.RETAIL,
  };
}

export function isClaimOnly(
  draftRegistration: DraftRegistration | undefined,
): boolean {
  return (
    draftRegistration?.source?.purchaseChannel === PurchaseChannel.RETAIL &&
    draftRegistration?.source?.isClaimOnly
  );
}

export function isMerchantCreated(
  draftRegistration: DraftRegistration | undefined,
): boolean {
  return (
    draftRegistration?.source?.purchaseChannel === PurchaseChannel.RETAIL &&
    draftRegistration?.source?.isMerchantCreated
  );
}

export function addCantFindItemDetails(
  draftRegistration: DraftRegistration,
  details: { productName: string; photos: string[]; price?: string },
): DraftRegistration {
  return { ...draftRegistration, cantFindItemDetails: details };
}

export function isCantFindItem(
  draftRegistration: DraftRegistration | undefined,
): boolean {
  return !!draftRegistration?.cantFindItemDetails;
}
